import React from 'react';
import { Image } from '../../ui/Image';

// Brand names: shown as they are spelled, never translated.
const SOURCE_NAMES = {
  vanta: 'Vanta',
  tenable: 'Tenable',
  drata: 'Drata',
  awsSecurityDigest: 'AWS Security Digest',
  mintlify: 'Mintlify',
  jira: 'Jira',
  servicenow: 'ServiceNow',
  clickup: 'ClickUp',
  linear: 'Linear',
  jamf: 'Jamf',
  cloudNews: 'Cloud news',
};

interface SourceConfig {
  image?: React.ReactNode;
}

/**
 * Maps source names to their corresponding configurations
 * Uses only image properties for consistent rendering
 */
const getSourceConfig = (source: string): SourceConfig => {
  const configMap: Record<string, SourceConfig> = {
    // Pleri source - same as plerion
    pleri: {
      image: (
        <Image type="plerionLogo" name="logo-small" width={20} height={20} />
      ),
    },
    // Plerion source
    plerion: {
      image: <Image type="plerionLogo" name="logo-small" className="!w-auto" />,
    },
    // Vanta source
    vanta: {
      image: (
        <Image
          src="logos/vanta.png"
          alt={SOURCE_NAMES.vanta}
          width={20}
          height={20}
        />
      ),
    },
    tenable: {
      image: (
        <Image
          src="logos/tenable-small.svg"
          alt={SOURCE_NAMES.tenable}
          width={20}
          height={20}
        />
      ),
    },
    // Drata source
    drata: {
      image: (
        <Image
          src="logos/drata.svg"
          alt={SOURCE_NAMES.drata}
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // AWS Security Digest
    awssecuritydigest: {
      image: (
        <Image
          src="logos/awssecuritydigest.png"
          alt={SOURCE_NAMES.awsSecurityDigest}
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // AWS Eye
    awseye: {
      image: (
        <Image
          name="aws-eye"
          type="resourceType"
          fileExtension="gif"
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // Slack
    slack: {
      image: (
        <Image
          name="slack-secret"
          type="resourceType"
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // Mintlify
    mintlify: {
      image: (
        <Image
          src="logos/mintlify.svg"
          alt={SOURCE_NAMES.mintlify}
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // Jira
    jira: {
      image: (
        <Image
          src="logos/jira.svg"
          alt={SOURCE_NAMES.jira}
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // ServiceNow
    servicenow: {
      image: (
        <Image
          src="logos/service-now.png"
          alt={SOURCE_NAMES.servicenow}
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // ClickUp
    clickup: {
      image: (
        <Image
          src="logos/clickup.png"
          alt={SOURCE_NAMES.clickup}
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // Linear
    linear: {
      image: (
        <Image
          src="logos/linear.png"
          alt={SOURCE_NAMES.linear}
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // Jamf
    jamf: {
      image: (
        <Image
          src="logos/jamf.svg"
          alt={SOURCE_NAMES.jamf}
          className="!w-auto"
          width={20}
          height={20}
        />
      ),
    },
    // Cloud News
    cloudNews: {
      image: (
        <Image
          src="logos/cloud-news.png"
          alt={SOURCE_NAMES.cloudNews}
          width={20}
          height={20}
        />
      ),
    },
  };

  return configMap[source];
};

/**
 * Renders the source icon using direct Image components
 * Returns the configured Image component directly
 */
export const getSourceIcon = (source: string): React.ReactNode => {
  try {
    const config = getSourceConfig(source);

    // Return the configured image component directly
    if (config.image) {
      return React.cloneElement(
        config.image as React.ReactElement<{ className?: string }>,
        {
          className: `h-4 w-4 rounded object-contain ${(config.image as React.ReactElement<{ className?: string }>).props.className || ''}`,
        },
      );
    }

    // Fallback to default if no image configuration
    return null;
  } catch {
    return null;
  }
};
